import { ChangeEvent, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, SearchInput } from '@ds';
import { MdOutlineAdd } from 'react-icons/md';
import { AdminPageHeader, ConditionalRender, DataTable, DeleteModal } from '@ui';
import { AuthContext } from '@lp/core/auth';
import {
  BANNED_TAIL_NUMBERS_COLUMNS,
  getRenderBannedTailNumbersTableCell,
} from '@lp/features/admin/tables/BannedTailNumbersListTable';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { AdminPermissions } from '@/permissions';
import { useBannedTailNumbersListQuery, useRemoveBannedTailNumberMutation } from '@/queries/banned-tail-numbers';
import { useFilterQueryWithParams } from '@/hooks/useFilterQueryWithParams';

export const BannedTailNumbersList = () => {
  const navigate = useNavigate();
  const {
    data,
    isLoading,
    isFetching,
    page,
    itemsPerPage,
    searchText,
    sortBy,
    setPage,
    setItemsPerPage,
    setSearchText,
    setSortBy,
  } = useFilterQueryWithParams({
    filterList: {},
    query: useBannedTailNumbersListQuery,
  });

  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.ProvidersConfigurationCreate);
  const hasEditPermissions = checkPermission(AdminPermissions.ProvidersConfigurationUpdate);

  const { isOpen, onModalOpen, onModalClose, onEntityDelete } = useEntityDelete({
    query: useRemoveBannedTailNumberMutation,
    successMessage: 'Banned Tail Number deleted successfully.',
  });

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleCreate = () => {
    navigate('/admin/configuration/banned_tail_numbers/create');
  };

  const handleEdit = useCallback(
    (bannedTailNumberId: string) => {
      navigate(`/admin/configuration/banned_tail_numbers/${bannedTailNumberId}`);
    },
    [navigate],
  );

  const renderBannedTailNumbersTableCell = useMemo(() => {
    return getRenderBannedTailNumbersTableCell(handleEdit, onModalOpen, hasCreatePermissions);
  }, [handleEdit, onModalOpen, hasCreatePermissions]);

  const BANNED_TAIL_NUMBERS_COLUMNS_FILTERED = useMemo(() => {
    return hasCreatePermissions || hasEditPermissions
      ? BANNED_TAIL_NUMBERS_COLUMNS
      : BANNED_TAIL_NUMBERS_COLUMNS.filter((item) => item.key !== 'actions');
  }, [hasCreatePermissions, hasEditPermissions]);

  return (
    <>
      <div className="flex flex-col place-items-stretch gap-9">
        <AdminPageHeader title="Banned Tail Numbers">
          <div className="max-w-[480px] grow">
            <SearchInput placeholder="Search" value={searchText} onChange={onSearchChange} inputVariant="secondary" />
          </div>
          <ConditionalRender show={hasCreatePermissions}>
            <div className="shrink-0">
              <Button size="md" variant="solid" endContent={<MdOutlineAdd size={18} />} onClick={handleCreate}>
                Create
              </Button>
            </div>
          </ConditionalRender>
        </AdminPageHeader>
        <div className="flex flex-col gap-4">
          <DataTable
            label="Banned Tail Nubers"
            columns={BANNED_TAIL_NUMBERS_COLUMNS_FILTERED}
            renderCell={renderBannedTailNumbersTableCell}
            data={data?.items || []}
            isLoading={isLoading}
            isFetching={isFetching}
            totalItems={data?.totalItems || 0}
            totalPages={data?.totalPages || 1}
            page={page}
            itemsPerPage={itemsPerPage}
            sortBy={sortBy}
            onPageChange={setPage}
            onItemsPerPageChange={setItemsPerPage}
            onSortByChange={setSortBy}
          />
        </div>
      </div>
      <DeleteModal
        title="Delete Banned Tail Number?"
        description="Are you sure you want to delete the Banned Tail Number?"
        isOpen={isOpen}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
    </>
  );
};
